/**
 * Family Relationship Validator
 * Validates family relationships to prevent conflicts and cycles
 */

export const RELATIONSHIP_TYPES = {
  FATHER: 'fk_vater_id',
  MOTHER: 'fk_mutter_id',
  PARTNER: 'fk_partner_id'
};

export const CONFLICT_TYPES = {
  ROLE_CONFLICT: 'ROLE_CONFLICT',
  CYCLE_DETECTED: 'CYCLE_DETECTED',
  SELF_REFERENCE: 'SELF_REFERENCE',
  BIDIRECTIONAL_CONFLICT: 'BIDIRECTIONAL_CONFLICT'
};

export const SEVERITY_LEVELS = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Configuration for which role combinations are forbidden
export const FORBIDDEN_ROLE_COMBINATIONS = [
  [RELATIONSHIP_TYPES.FATHER, RELATIONSHIP_TYPES.MOTHER], // Can't be both parents
  [RELATIONSHIP_TYPES.FATHER, RELATIONSHIP_TYPES.PARTNER], // Can't be father and partner
  [RELATIONSHIP_TYPES.MOTHER, RELATIONSHIP_TYPES.PARTNER]  // Can't be mother and partner
];

export class FamilyRelationshipValidator {
  constructor(allClients = []) {
    this.allClients = allClients;
    this.clientsMap = new Map();
    this.buildClientsMap();
  }

  /**
   * Build a map for faster client lookups
   */
  buildClientsMap() {
    this.clientsMap.clear();
    this.allClients.forEach(client => {
      this.clientsMap.set(client.id, client);
    });
  }

  /**
   * Update the clients data
   * @param {Array} allClients - Array of all client objects
   */
  updateClients(allClients) {
    this.allClients = allClients;
    this.buildClientsMap();
  }

  /**
   * Main validation entry point
   * @param {number} clientId - The client being edited
   * @param {string} relationshipType - Type of relationship (FATHER, MOTHER, PARTNER)
   * @param {number} targetClientId - The client being linked as the relationship
   * @param {Object} currentClientData - Current state of the client being edited
   * @returns {Object} Validation result: { valid: boolean, conflicts: [], suggestions: [] }
   */
  validateRelationship(clientId, relationshipType, targetClientId, currentClientData) {
    const conflicts = [];

    // Basic validation
    const basicConflicts = this.checkBasicValidation(clientId, relationshipType, targetClientId);
    conflicts.push(...basicConflicts);

    // Check direct conflicts (same person in multiple roles)
    const directConflicts = this.checkDirectConflicts(clientId, relationshipType, targetClientId, currentClientData);
    conflicts.push(...directConflicts);

    // Check for cycles
    const cycleConflicts = this.checkCycles(clientId, relationshipType, targetClientId, currentClientData);
    conflicts.push(...cycleConflicts);

    // Check bidirectional conflicts
    const bidirectionalConflicts = this.checkBidirectionalConflicts(clientId, relationshipType, targetClientId);
    conflicts.push(...bidirectionalConflicts);

    const hasErrors = conflicts.some(conflict => conflict.severity === SEVERITY_LEVELS.ERROR);

    return {
      valid: !hasErrors,
      conflicts,
      suggestions: this.generateSuggestions(conflicts)
    };
  }

  /**
   * Check basic validation rules
   */
  checkBasicValidation(clientId, relationshipType, targetClientId) {
    const conflicts = [];

    // Check if target client exists
    if (!this.clientsMap.has(targetClientId)) {
      conflicts.push({
        type: 'CLIENT_NOT_FOUND',
        severity: SEVERITY_LEVELS.ERROR,
        message: 'Ausgewählter Klient existiert nicht'
      });
      return conflicts; // Early return if client doesn't exist
    }

    // Prevent self-reference
    if (clientId === targetClientId) {
      conflicts.push({
        type: CONFLICT_TYPES.SELF_REFERENCE,
        severity: SEVERITY_LEVELS.ERROR,
        relationshipType,
        targetClientId,
        message: 'Ein Klient kann nicht mit sich selbst verwandt sein'
      });
    }

    // Check if relationship type is valid
    if (!Object.values(RELATIONSHIP_TYPES).includes(relationshipType)) {
      conflicts.push({
        type: 'INVALID_RELATIONSHIP_TYPE',
        severity: SEVERITY_LEVELS.ERROR,
        message: `Ungültiger Beziehungstyp: ${relationshipType}`
      });
    }

    return conflicts;
  }

  /**
   * Check if target is already in another conflicting role
   */
  checkDirectConflicts(clientId, relationshipType, targetClientId, currentClientData) {
    const conflicts = [];
    
    // Get existing roles for the target client
    const existingRoles = this.getExistingRoles(targetClientId, currentClientData);
    
    for (const existingRole of existingRoles) {
      if (this.isConflictingRole(relationshipType, existingRole)) {
        const targetClient = this.clientsMap.get(targetClientId);
        conflicts.push({
          type: CONFLICT_TYPES.ROLE_CONFLICT,
          severity: SEVERITY_LEVELS.ERROR,
          relationshipType,
          existingRole,
          targetClientId,
          targetClientName: `${targetClient?.nachname || ''} ${targetClient?.vorname || ''}`.trim(),
          message: `${targetClient?.nachname || ''} ${targetClient?.vorname || ''} ist bereits als ${this.getRoleDisplayName(existingRole)} verknüpft`
        });
      }
    }

    return conflicts;
  }

  /**
   * Check for cycles in family relationships
   */
  checkCycles(clientId, relationshipType, targetClientId, currentClientData, maxDepth = 3) {
    const conflicts = [];

    // Create a temporary updated client data for cycle detection
    const tempClientData = { ...currentClientData };
    tempClientData[relationshipType] = targetClientId;

    // Check if this would create a cycle
    if (this.wouldCreateCycle(clientId, targetClientId, tempClientData, maxDepth)) {
      const targetClient = this.clientsMap.get(targetClientId);
      conflicts.push({
        type: CONFLICT_TYPES.CYCLE_DETECTED,
        severity: SEVERITY_LEVELS.ERROR,
        relationshipType,
        targetClientId,
        targetClientName: `${targetClient?.nachname || ''} ${targetClient?.vorname || ''}`.trim(),
        message: `Diese Verknüpfung würde einen Familienzyklus erstellen`
      });
    }

    return conflicts;
  }

  /**
   * Check bidirectional relationship conflicts
   */
  checkBidirectionalConflicts(clientId, relationshipType, targetClientId) {
    const conflicts = [];
    const targetClient = this.clientsMap.get(targetClientId);
    
    if (!targetClient) return conflicts;

    // Check if target already has the current client as a parent
    if ((relationshipType === RELATIONSHIP_TYPES.FATHER || relationshipType === RELATIONSHIP_TYPES.MOTHER) &&
        (targetClient.fk_vater_id === clientId || targetClient.fk_mutter_id === clientId)) {
      
      conflicts.push({
        type: CONFLICT_TYPES.BIDIRECTIONAL_CONFLICT,
        severity: SEVERITY_LEVELS.ERROR,
        relationshipType,
        targetClientId,
        targetClientName: `${targetClient.nachname || ''} ${targetClient.vorname || ''}`.trim(),
        message: `${targetClient.nachname || ''} ${targetClient.vorname || ''} ist bereits als Kind verknüpft`
      });
    }

    return conflicts;
  }

  /**
   * Get existing roles for a target client in the current client data
   */
  getExistingRoles(targetClientId, currentClientData) {
    const roles = [];
    
    if (currentClientData.fk_vater_id === targetClientId) {
      roles.push(RELATIONSHIP_TYPES.FATHER);
    }
    if (currentClientData.fk_mutter_id === targetClientId) {
      roles.push(RELATIONSHIP_TYPES.MOTHER);
    }
    if (currentClientData.fk_partner_id === targetClientId) {
      roles.push(RELATIONSHIP_TYPES.PARTNER);
    }
    
    return roles;
  }

  /**
   * Check if two roles conflict with each other
   */
  isConflictingRole(role1, role2) {
    if (role1 === role2) return false; // Same role is not a conflict
    
    return FORBIDDEN_ROLE_COMBINATIONS.some(combination => 
      (combination.includes(role1) && combination.includes(role2))
    );
  }

  /**
   * Check if adding a relationship would create a cycle
   */
  wouldCreateCycle(startClientId, targetClientId, tempClientData, maxDepth = 3) {
    const visited = new Set();
    
    const hasPath = (fromId, toId, depth = 0) => {
      if (depth > maxDepth) return false;
      if (visited.has(fromId)) return false;
      if (fromId === toId) return true;
      
      visited.add(fromId);
      
      const client = fromId === startClientId ? tempClientData : this.clientsMap.get(fromId);
      if (!client) return false;
      
      // Check if we can reach toId through parent relationships
      const parents = [client.fk_vater_id, client.fk_mutter_id].filter(id => id !== null && id !== undefined);
      
      for (const parentId of parents) {
        if (hasPath(parentId, toId, depth + 1)) {
          return true;
        }
      }
      
      return false;
    };

    // Check if we can reach startClientId from targetClientId
    // This would mean adding the relationship creates a cycle
    return hasPath(targetClientId, startClientId);
  }

  /**
   * Get display name for a relationship role
   */
  getRoleDisplayName(role) {
    const roleNames = {
      [RELATIONSHIP_TYPES.FATHER]: 'Vater',
      [RELATIONSHIP_TYPES.MOTHER]: 'Mutter',
      [RELATIONSHIP_TYPES.PARTNER]: 'Partner'
    };
    
    return roleNames[role] || role;
  }

  /**
   * Get a client by ID
   */
  getClientById(clientId) {
    return this.clientsMap.get(clientId);
  }

  /**
   * Generate suggestions based on conflicts
   */
  generateSuggestions(conflicts) {
    const suggestions = [];
    
    if (conflicts.some(c => c.type === CONFLICT_TYPES.ROLE_CONFLICT)) {
      suggestions.push({
        type: 'REMOVE_EXISTING_ROLE',
        message: 'Entfernen Sie zuerst die bestehende Verknüpfung'
      });
    }
    
    if (conflicts.some(c => c.type === CONFLICT_TYPES.CYCLE_DETECTED)) {
      suggestions.push({
        type: 'CHECK_FAMILY_TREE',
        message: 'Überprüfen Sie die Familienstruktur auf zirkuläre Verknüpfungen'
      });
    }
    
    return suggestions;
  }

  /**
   * Get filtered options for a relationship type
   * @param {number} clientId - The client being edited
   * @param {string} relationshipType - Type of relationship
   * @param {Object} currentClientData - Current state of the client
   * @returns {Object} { available: [], filtered: [], reasons: Map }
   */
  getFilteredOptions(clientId, relationshipType, currentClientData) {
    const available = [];
    const filtered = [];
    const reasons = new Map();

    for (const client of this.allClients) {
      if (client.id === clientId) continue; // Skip self

      const validation = this.validateRelationship(clientId, relationshipType, client.id, currentClientData);
      
      if (validation.valid) {
        available.push(client);
      } else {
        filtered.push(client);
        reasons.set(client.id, validation.conflicts.map(c => c.message));
      }
    }

    return { available, filtered, reasons };
  }
}

export default FamilyRelationshipValidator;